import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { HowDidYouHearAboutUs } from '../../patient.types';
import type { IEPStatus, PrimarySupportArea, AvailabilitySchedule, TelehealthPreference } from '../types/index';
import type { UserDataResponse, PatientUserData } from '../types/user-data.types';
import type { PartialClientSignupResponse } from '../types/partial-user-data.types';
import { convertAvailabilityScheduleFromBackend, convertAvailabilityScheduleToBackend } from '../utils/availability-schedule-util';


export interface RegistrationFormData {
  // Guardian/Initial Info
  guardianFirstName: string;
  guardianLastName: string;
  email: string;
  mobile: string;
  howDidYouHearAboutUs?: HowDidYouHearAboutUs;
  howDidYouHearAboutUsText?: string;
  privacyConsent: boolean;
  
  // Client Info
  firstName: string;
  lastName: string;
  dob: string;
  zipCode: string;
  password: string;
  confirmPassword: string;
  communicationOptIn: boolean;
  
  // Personal Questions Form Data
  isInSchoolDaycare?: boolean;
  hasIEP?: IEPStatus;
  hasHadABAServices?: boolean;
  primarySupportAreas?: PrimarySupportArea[];
  availabilitySchedule?: AvailabilitySchedule;
  openToTelehealth?: TelehealthPreference;
  
  // Insurance Files - Array format matching API response
  insuranceFiles?: Array<{
    id?: string;
    isActive?: boolean;
    createdAt?: string;
    updatedAt?: string;
    userId?: string;
    frontImg: string;
    backImg: string;
    vobImg?: string | null;
    isVerified?: boolean;
    isPrimary: boolean;
    verifyNotes?: string | null;
  }>;
  
  // Diagnostic Form Data
  hasAutismDiagnosis?: boolean;
  diagnosticEvaluationFile?: File | null;
  letterOfMedicalNecessityFile?: File | null;
  cdeUrl?: string | null;
  lmnUrl?: string | null;
  diagnosticProviderInfo?: {
    providerGroup?: string;
    providerName?: string;
    providerPhone?: string;
  };
  letterProviderInfo?: {
    providerGroup?: string;
    providerName?: string;
    providerPhone?: string;
  };


}

interface RegistrationContextType {
  formData: RegistrationFormData;
  updateFormData: (data: Partial<RegistrationFormData>) => void;
  clearFormData: () => void;
  populateFormWithUserData: (userData: UserDataResponse | PartialClientSignupResponse) => void;
}

const RegistrationContext = createContext<RegistrationContextType | undefined>(undefined);

export const useRegistration = () => {
  const context = useContext(RegistrationContext);
  if (!context) {
    throw new Error('useRegistration must be used within a RegistrationProvider');
  }
  return context;
};



interface RegistrationProviderProps {
  children: ReactNode;
}

export const RegistrationProvider: React.FC<RegistrationProviderProps> = ({ children }) => {
  // Initialize form data from localStorage if available, otherwise use defaults
  const getInitialFormData = (): RegistrationFormData => {
    try {
      const savedFormData = localStorage.getItem('registration_form_data');
      if (savedFormData) {
        const parsed = JSON.parse(savedFormData);
        // Only restore if the data is recent (within last hour)
        const savedTime = localStorage.getItem('registration_form_timestamp');
        if (savedTime && Date.now() - parseInt(savedTime) < 3600000) {
                return {
        guardianFirstName: '',
        guardianLastName: '',
        email: '',
        mobile: '',
        howDidYouHearAboutUs: undefined,
        howDidYouHearAboutUsText: '',
        privacyConsent: false,
        firstName: '',
        lastName: '',
        dob: '',
        zipCode: '',
        password: '',
        confirmPassword: '',
        communicationOptIn: false,
        ...parsed
      };
        }
      }
    } catch (error) {
      console.warn('Failed to load saved form data:', error);
    }
    
    return {
      guardianFirstName: '',
      guardianLastName: '',
      email: '',
      mobile: '',
      howDidYouHearAboutUs: undefined,
      howDidYouHearAboutUsText: '',
      privacyConsent: false,
      firstName: '',
      lastName: '',
      dob: '',
      zipCode: '',
      password: '',
      confirmPassword: '',
      communicationOptIn: false,
    };
  };

  const [formData, setFormData] = useState<RegistrationFormData>(getInitialFormData);

  const updateFormData = useCallback((data: Partial<RegistrationFormData>) => {
    setFormData(prev => {
      const newFormData = { ...prev, ...data };
      // Save to localStorage
      try {
        localStorage.setItem('registration_form_data', JSON.stringify(newFormData));
        localStorage.setItem('registration_form_timestamp', Date.now().toString());
      } catch (error) {
        console.warn('Failed to save form data:', error);
      }
      return newFormData;
    });
  }, []);

  const clearFormData = useCallback(() => {
    setFormData({
      guardianFirstName: '',
      guardianLastName: '',
      email: '',
      mobile: '',
      howDidYouHearAboutUs: undefined,
      howDidYouHearAboutUsText: '',
      privacyConsent: false,
      firstName: '',
      lastName: '',
      dob: '',
      zipCode: '',
      password: '',
      confirmPassword: '',
      communicationOptIn: false,
    });
    // Clear localStorage
    try {
      localStorage.removeItem('registration_form_data');
      localStorage.removeItem('registration_form_timestamp');
    } catch (error) {
      console.warn('Failed to clear form data:', error);
    }
  }, []);

  // Centralized function to populate form with user data
  const populateFormWithUserData = useCallback((userData: UserDataResponse | PartialClientSignupResponse) => {
    if (!userData) return;

    console.log('📝 Populating form with user data:', userData);

    // Handle partial signup data (which doesn't have a 'type' field)
    if (!('type' in userData)) {
      console.log('📝 Processing partial signup data');
      const partialData = userData as PartialClientSignupResponse;
      const formDataToUpdate: Partial<RegistrationFormData> = {
        // Guardian/Initial Info - for partial signup, guardian names are the main names
        guardianFirstName: partialData.guardianFirstName || '',
        guardianLastName: partialData.guardianLastName || '',
        email: partialData.email || '',
        mobile: partialData.mobile || '',
        howDidYouHearAboutUs: partialData.howDidYouHearAboutUs,
        howDidYouHearAboutUsText: partialData.howDidYouHearAboutUsText || '',
        privacyConsent: false,
        communicationOptIn: partialData.communicationOptIn ?? true,
      };

      console.log('📝 Updated form data from partial signup:', formDataToUpdate);
      updateFormData(formDataToUpdate);
      return;
    }

    // Handle regular user data (patient/family users)
    if (userData.type !== 'PF') {
      console.log('⚠️ User is not a patient/family user, skipping form population');
      return;
    }

    const patientData = userData as PatientUserData;
    const formDataToUpdate: Partial<RegistrationFormData> = {
      // Guardian/Initial Info
      guardianFirstName: patientData.guardianFirstName || '',
      guardianLastName: patientData.guardianLastName || '',
      email: patientData.email || '',
      mobile: patientData.mobile || '',
      howDidYouHearAboutUs: patientData.howDidYouHearAboutUs,
      howDidYouHearAboutUsText: patientData.howDidYouHearAboutUsText || '',
      privacyConsent: false, // Not in API response, default to false
      
      // Client Info
      firstName: patientData.firstName || '',
      lastName: patientData.lastName || '',
      dob: patientData.dob || '',
      zipCode: patientData.zipCode || '',
      communicationOptIn: patientData.communicationOptIn ?? false,
      
      // Personal Questions Form Data
      isInSchoolDaycare: patientData.isInSchoolDaycare,
      hasIEP: patientData.hasIEP,
      hasHadABAServices: patientData.hasHadABAServices,
      primarySupportAreas: patientData.primarySupportAreas,
      openToTelehealth: patientData.openToTelehealth,
      
      // Diagnostic Form Data
      hasAutismDiagnosis: patientData.autismDiagnosis === 'YES' ? true : 
                          patientData.autismDiagnosis === 'NO' ? false : undefined,
      diagnosticProviderInfo: patientData.diagnosticProviderInfo || undefined,
      letterProviderInfo: patientData.letterProviderInfo || undefined,
      cdeUrl: patientData.cdeUrl || undefined,
      lmnUrl: patientData.lmnUrl || undefined,
      
      // Insurance Files
      insuranceFiles: patientData.insuranceFiles,
    };

    // Handle availability schedule conversion
    if (patientData.availabilitySchedule) {
      formDataToUpdate.availabilitySchedule = convertAvailabilityScheduleFromBackend(patientData.availabilitySchedule);
    }

    // Check if user has diagnostic info and show appropriate screen
    // Note: autismDiagnosis is not in the user profile API, so we'll handle this
    // in the FamilyRegistration component when checking diagnostic info separately

    console.log('📝 Updated form data from regular user:', formDataToUpdate);
    updateFormData(formDataToUpdate);
  }, [updateFormData]);

  return (
    <RegistrationContext.Provider value={{
      formData,
      updateFormData,
      clearFormData,
      populateFormWithUserData,
    }}>
      {children}
    </RegistrationContext.Provider>
  );
};

 